import React, { useEffect } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useMSRNudgeRecommendations } from '@atlassian/cross-join-nudges';
import { useSubProduct } from '@atlassian/jira-subproduct/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import { useJpdCrossJoinNudgeEligibility } from './eligibility/jpd-eligibility.tsx';
import { useConfluenceCrossJoinNudgeEligibility } from './eligibility/confluence-eligibility.tsx';
import { useJpdCrossJoinNudge } from './nudges/jpd-nudge.tsx';
import { useLoomCrossJoinNudge } from './nudges/loom-nudge.tsx';
import { useConfluenceCrossJoinNudge } from './nudges/confluence-nudge.tsx';
import { useLoomCrossJoinNudgeEligibility } from './eligibility/loom-eligibility.tsx';

/**
 * CrossJoinNudges conditionally renders various prompts based on user and site context.
 * Rendering occurs in a side-effectful way as targeting and eligibility checks are made synchronously at runtime.
 */
export const CrossJoinNudges = (): React.JSX.Element => {
	return (
		<JSErrorBoundary id="JiraCrossJoinNudges" teamName="groot" packageName="jiraCrossJoinNudges">
			<CrossJoinNudgesWithoutErrorBoundary />
		</JSErrorBoundary>
	);
};

/**
 * useCrossJoinNudges
 * @param execNudge is a temporary prop used to safely feature-gate the gate groot-1154-cjn-global-home
 * @deprecated use CrossJoinNudges instead
 */
export const useCrossJoinNudges = (execNudge = true): void => {
	const cloudId = useCloudId();
	const subProduct = useSubProduct();
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({
		product: 'jira',
		sessionId: crypto.randomUUID(),
		cloudId,
		subProduct: subProduct || '',
	});

	const getJpdCrossJoinNudgeEligibility = useJpdCrossJoinNudgeEligibility({
		getMSRNudgeRecommendations,
	});
	const getLoomCrossJoinNudgeEligibility = useLoomCrossJoinNudgeEligibility();
	const getConfluenceCrossJoinNudgeEligibility = useConfluenceCrossJoinNudgeEligibility({
		getMSRNudgeRecommendations,
	});
	const showJpdNudge = useJpdCrossJoinNudge();
	const showLoomNudge = useLoomCrossJoinNudge();
	const showConfluenceNudge = useConfluenceCrossJoinNudge();

	useEffect(() => {
		async function executeNudges() {
			const loomEligibilityResult = await getLoomCrossJoinNudgeEligibility();
			if (loomEligibilityResult.isEligible) {
				showLoomNudge();
				return;
			}

			const jpdEligibilityResult = await getJpdCrossJoinNudgeEligibility();
			if (jpdEligibilityResult.isEligible) {
				showJpdNudge(jpdEligibilityResult.data);
				return;
			}

			const confluenceEligibilityResult = await getConfluenceCrossJoinNudgeEligibility();
			if (confluenceEligibilityResult.isEligible) {
				showConfluenceNudge(confluenceEligibilityResult.data);
			}
		}

		if (execNudge) {
			executeNudges();
		}
	}, [
		execNudge,
		getConfluenceCrossJoinNudgeEligibility,
		getJpdCrossJoinNudgeEligibility,
		getLoomCrossJoinNudgeEligibility,
		showConfluenceNudge,
		showJpdNudge,
		showLoomNudge,
	]);
};

const CrossJoinNudgesWithoutErrorBoundary = () => {
	useCrossJoinNudges(true);

	return null;
};

export default CrossJoinNudges;
